import React, { useCallback, useEffect, useState } from 'react';
import restaurantService, {
  OrderFeedbackContext,
  OrderFeedbackFormData,
  OrderFeedbackSummary,
} from '../../services/restaurantService';
import './OrderFeedback.css';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  readonly?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({ value, onChange, readonly }) => (
  <div className={`star-rating ${readonly ? 'readonly' : ''}`}>
    {[1, 2, 3, 4, 5].map((star) => (
      <button
        key={star}
        type="button"
        className={star <= value ? 'active' : ''}
        aria-label={`${star} star${star !== 1 ? 's' : ''}`}
        onClick={() => !readonly && onChange?.(star)}
      >
        <i className={`fa ${star <= value ? 'fa-star' : 'fa-star-o'}`} aria-hidden />
      </button>
    ))}
  </div>
);

const RATING_FIELDS: Array<{ key: keyof OrderFeedbackFormData; label: string }> = [
  { key: 'restaurantRating', label: 'Restaurant' },
  { key: 'foodQualityRating', label: 'Food quality' },
  { key: 'ambienceRating', label: 'Ambience' },
  { key: 'valueForMoneyRating', label: 'Value for money' },
  { key: 'orderAccuracyRating', label: 'Order accuracy' },
  { key: 'speedRating', label: 'Wait time / speed' },
  { key: 'packagingRating', label: 'Packaging quality' },
  { key: 'deliveryRating', label: 'Delivery experience' },
  { key: 'billingRating', label: 'Billing / checkout' },
  { key: 'captainRating', label: 'Captain / service' },
];

const emptyForm = (): OrderFeedbackFormData => ({
  overallRating: 0,
  restaurantRating: 0,
  foodQualityRating: 0,
  ambienceRating: 0,
  captainRating: 0,
  valueForMoneyRating: 0,
  orderAccuracyRating: 0,
  speedRating: 0,
  packagingRating: 0,
  deliveryRating: 0,
  billingRating: 0,
  recommendScore: null,
  comment: '',
  itemRatings: [],
});

interface OrderFeedbackPanelProps {
  appName: string;
  orderId: number;
  readonly?: boolean;
  compact?: boolean;
  onSubmitted?: (summary: OrderFeedbackSummary) => void;
  onNotify?: (message: string, type: 'success' | 'error' | 'info') => void;
}

const OrderFeedbackPanel: React.FC<OrderFeedbackPanelProps> = ({
  appName,
  orderId,
  readonly: readonlyProp,
  compact,
  onSubmitted,
  onNotify,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [context, setContext] = useState<OrderFeedbackContext | null>(null);
  const [form, setForm] = useState<OrderFeedbackFormData>(emptyForm());

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const res = await restaurantService.getOrderFeedback(appName, orderId);
      if (res.success) {
        setContext(res);
        if (res.feedback) {
          const fb = res.feedback;
          setForm({
            overallRating: fb.overallRating || 0,
            restaurantRating: fb.restaurantRating || 0,
            foodQualityRating: fb.foodQualityRating || 0,
            ambienceRating: fb.ambienceRating || 0,
            captainRating: fb.captainRating || 0,
            valueForMoneyRating: fb.valueForMoneyRating || 0,
            orderAccuracyRating: fb.orderAccuracyRating || 0,
            speedRating: fb.speedRating || 0,
            packagingRating: fb.packagingRating || 0,
            deliveryRating: fb.deliveryRating || 0,
            billingRating: fb.billingRating || 0,
            recommendScore: fb.recommendScore ?? null,
            comment: fb.comment || '',
            itemRatings: (fb.itemRatings || []).map((item) => ({
              menuId: item.menuId,
              rating: item.rating || 0,
              comment: item.comment || '',
              itemName: item.itemName,
            })),
          });
        } else if (res.orderItems) {
          setForm((prev) => ({
            ...prev,
            itemRatings: res.orderItems!.map((item) => ({
              menuId: item.menuId,
              rating: 0,
              comment: '',
              itemName: item.itemName,
            })),
          }));
        }
      }
    } catch {
      onNotify?.('Could not load feedback form', 'error');
    } finally {
      setLoading(false);
    }
  }, [appName, orderId, onNotify]);

  useEffect(() => {
    loadContext();
  }, [loadContext]);

  const isReadonly =
    readonlyProp ||
    !context?.eligible ||
    (context?.submitted && !context?.canEdit);

  const setRating = (key: keyof OrderFeedbackFormData, value: number) => {
    if (isReadonly) return;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setItemRating = (menuId: number, rating: number) => {
    if (isReadonly) return;
    setForm((prev) => ({
      ...prev,
      itemRatings: prev.itemRatings.map((item) =>
        item.menuId === menuId ? { ...item, rating } : item
      ),
    }));
  };

  const handleSubmit = async () => {
    if (form.overallRating < 1) {
      onNotify?.('Please rate your overall experience', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        itemRatings: form.itemRatings
          .filter((item) => (item.rating ?? 0) > 0)
          .map(({ menuId, rating, comment }) => ({ menuId, rating: rating ?? 0, comment })),
      };
      const isUpdate = Boolean(context?.submitted);
      const res = isUpdate
        ? await restaurantService.updateOrderFeedback(appName, orderId, payload)
        : await restaurantService.submitOrderFeedback(appName, orderId, payload);
      if (res.success) {
        onNotify?.(res.message || 'Thank you for your feedback!', 'success');
        if (res.feedbackSummary) {
          onSubmitted?.(res.feedbackSummary);
        }
        await loadContext();
      } else {
        onNotify?.(res.message || 'Could not submit feedback', 'error');
      }
    } catch (err: any) {
      onNotify?.(err.response?.data?.message || 'Could not submit feedback', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="order-feedback-loading"><i className="fa fa-spinner fa-spin" aria-hidden /> Loading feedback...</div>;
  }

  if (!context) {
    return null;
  }

  if (!context.eligible && !context.submitted) {
    return null;
  }

  const applicable = context.applicableFields || {};
  const visibleFields = RATING_FIELDS.filter(
    (field) => applicable[field.key] !== false
  );

  return (
    <div className={`order-feedback-panel ${isReadonly ? 'readonly' : ''}`} id="order-feedback-section">
      <h3>{context.submitted ? 'Your feedback' : 'Rate your experience'}</h3>
      <p className="order-feedback-subtitle">
        {context.submitted
          ? context.canEdit
            ? `You can edit your feedback for ${context.daysRemaining} more day${context.daysRemaining !== 1 ? 's' : ''}.`
            : 'Thank you — your feedback helps us improve.'
          : `Share your experience within ${context.daysRemaining} day${context.daysRemaining !== 1 ? 's' : ''}.`}
      </p>

      {!compact && (
        <div className="order-feedback-section">
          <div className="order-feedback-section-title">Overall</div>
          <div className="order-feedback-row">
            <label>How was your overall experience?</label>
            <StarRating
              value={form.overallRating}
              onChange={(v) => setRating('overallRating', v)}
              readonly={isReadonly}
            />
          </div>
          <div className="order-feedback-row">
            <label>Would you recommend us? (0–10)</label>
          </div>
          <div className={`order-feedback-nps ${isReadonly ? 'readonly' : ''}`}>
            {Array.from({ length: 11 }, (_, i) => i).map((score) => (
              <button
                key={score}
                type="button"
                className={form.recommendScore === score ? 'selected' : ''}
                onClick={() => !isReadonly && setForm((prev) => ({ ...prev, recommendScore: score }))}
              >
                {score}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="order-feedback-section">
        <div className="order-feedback-section-title">Restaurant experience</div>
        {visibleFields.map(({ key, label }) => (
          <div key={key} className="order-feedback-row">
            <label>
              {key === 'captainRating' && context.captain?.userName
                ? `${label} (${context.captain.userName})`
                : label}
            </label>
            <StarRating
              value={(form[key] as number) || 0}
              onChange={(v) => setRating(key, v)}
              readonly={isReadonly}
            />
          </div>
        ))}
      </div>

      {form.itemRatings.length > 0 && (
        <div className="order-feedback-section">
          <div className="order-feedback-section-title">Rate your dishes</div>
          {form.itemRatings.map((item) => (
            <div key={item.menuId} className="order-feedback-item-row">
              <span className="item-name">{item.itemName || `Item #${item.menuId}`}</span>
              <StarRating
                value={item.rating ?? 0}
                onChange={(v) => setItemRating(item.menuId, v)}
                readonly={isReadonly}
              />
            </div>
          ))}
        </div>
      )}

      <div className="order-feedback-section order-feedback-comment">
        <div className="order-feedback-section-title">Comments (optional)</div>
        <textarea
          value={form.comment}
          onChange={(e) => !isReadonly && setForm((prev) => ({ ...prev, comment: e.target.value }))}
          placeholder="Tell us more about your visit..."
          maxLength={500}
          readOnly={isReadonly}
        />
      </div>

      {!isReadonly && (
        <div className="order-feedback-actions">
          <button
            type="button"
            className="order-feedback-submit"
            disabled={submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Saving...' : context.submitted ? 'Update feedback' : 'Submit feedback'}
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderFeedbackPanel;
